import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  completeWfSetupTask,
  fetchWfSetupStatus,
} from "../../../Moduels/attendance/wfApi";

export default function WfOperationalOnboarding() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setErr("");
    try {
      const s = await fetchWfSetupStatus();
      setState(s);
    } catch (e) {
      setErr(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : e?.response?.data?.detail?.message || e.message || "Failed to load onboarding");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onRefresh = async () => {
    setBusy(true);
    await load();
    setBusy(false);
  };

  const onMarkComplete = async (taskCode) => {
    setBusy(true);
    setErr("");
    try {
      await completeWfSetupTask(taskCode);
      await load();
    } catch (e) {
      setErr(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : e?.response?.data?.detail?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!state) {
    return <div className="p-6 text-gray-500">Loading operational setup…</div>;
  }

  const stage = String(state.setup_stage || "PENDING").toUpperCase();
  const allDone = stage === "ACTIVE" && !state.pending_count;
  const tasks = state.tasks || [];

  return (
    <div className="p-6 w-full max-w-full min-w-0">
      <Link to="/attendance" className="text-sm text-teal-700 hover:underline mb-4 inline-block">
        ← Attendance home
      </Link>
      <h1 className="text-2xl font-semibold text-gray-800 mb-1">Module configuration</h1>
      <p className="text-sm text-gray-500 mb-4">
        {allDone
          ? "All steps for your selected attendance sources are complete."
          : "Complete each step for the sources you selected (e.g. Manual HR Entry)."}
      </p>
      <p className="text-xs font-medium text-slate-600 mb-4">
        Stage: <span className="text-teal-700">{stage}</span>
        {state.pending_count > 0 && (
          <span className="text-amber-700"> · {state.pending_count} task(s) pending</span>
        )}
      </p>

      {allDone && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-900">
          <p className="font-semibold">Manual HR Entry and module setup are complete.</p>
          <p className="mt-1 text-green-800">
            You can mark attendance, view records, and run payroll as usual.
          </p>
          <Link
            to="/attendance/mark"
            className="inline-block mt-3 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700"
          >
            Mark attendance →
          </Link>
        </div>
      )}

      {err && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{String(err)}</div>}

      {tasks.length === 0 && !allDone && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
          No module tasks were found. Finish the{" "}
          <Link to="/attendance/setup" className="underline font-medium">
            organisation setup wizard
          </Link>{" "}
          first, then reload this page.
        </div>
      )}

      <ul className="space-y-3 mb-6">
        {tasks.map((t) => (
          <li
            key={t.task_code}
            className="border rounded-xl p-4 bg-white flex flex-wrap items-center justify-between gap-2"
          >
            <div>
              <p className="font-medium">{t.title}</p>
              <p className="text-xs text-gray-500">
                {t.task_code} · {t.validation_state}
              </p>
            </div>
            <div className="flex gap-2">
              <Link to={t.route_path} className="px-3 py-1 text-sm border rounded-lg hover:bg-gray-50">
                Open
              </Link>
              {t.status !== "completed" && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onMarkComplete(t.task_code)}
                  className="px-3 py-1 text-sm bg-teal-600 text-white rounded-lg disabled:opacity-50"
                >
                  Mark done
                </button>
              )}
              {t.status === "completed" && (
                <span className="text-sm text-green-700 font-medium">Done</span>
              )}
            </div>
          </li>
        ))}
      </ul>

      <div className="flex gap-3">
        <button type="button" onClick={onRefresh} disabled={busy} className="px-4 py-2 border rounded-lg text-sm">
          Re-check all
        </button>
        {state.next_task_route && state.pending_count > 0 && (
          <button
            type="button"
            onClick={() => navigate(state.next_task_route)}
            className="flex-1 bg-teal-600 text-white py-2 rounded-lg text-sm"
          >
            Continue to next step →
          </button>
        )}
      </div>
    </div>
  );
}
